import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

interface DeleteRequest {
  id: string;
  entityType: 'OPPORTUNITY' | 'LEAD';
  entityId: string;
  requestedBy: string;
  requestReasonCode: string;
  requestNotes: string | null;
  approverRequired: 'MANAGER' | 'ADMIN';
  status: string;
  isErasureRequest: boolean;
  createdAt: string | null;
  expiresAt: string | null;
}

/**
 * Epic 86, Story 86.5 — Approval inbox for delete requests.
 *
 * Managers see MANAGER-tier rows; admins see both. The list is
 * pre-filtered to `status=PENDING` by the backend; approved/rejected
 * history is one click away (toggle below). Bulk actions in V1 are
 * sequential (no batch endpoint yet).
 */
export function DeletionRequestQueuePage() {
  const { t } = useTranslation();
  const [pending, setPending] = useState<DeleteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  function load() {
    setLoading(true);
    apiClient
      .get<DeleteRequest[]>('/delete-requests/pending')
      .then(({ data }) => setPending(data))
      .catch(() => setPending([]))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function decide(id: string, action: 'approve' | 'reject') {
    setBusy(id);
    setError(null);
    try {
      await apiClient.post(`/delete-requests/${id}/${action}`, {
        notes: reviewNotes[id] ?? null,
      });
      load();
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('deletionQueue.error_generic'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('deletionQueue.title')}</h1>
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          {t('common.refresh')}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('deletionQueue.subtitle')}
      </p>
      {error && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}
      {loading && (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}
      {!loading && pending.length === 0 && (
        <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
          {t('deletionQueue.empty')}
        </div>
      )}
      <div className="space-y-3">
        {pending.map((r) => (
          <div key={r.id} className="rounded-lg border bg-card p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={
                    r.entityType === 'OPPORTUNITY'
                      ? 'rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary'
                      : 'rounded bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-300'
                  }
                >
                  {r.entityType}
                </span>
                <span className="font-medium">
                  {t(`closeReason.code_${r.requestReasonCode.toLowerCase()}`)}
                </span>
                {r.isErasureRequest && (
                  <span className="rounded bg-red-500/10 px-2 py-0.5 text-xs font-medium text-red-300">
                    {t('deletionQueue.erasure_badge')}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {r.approverRequired}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">
                {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}
              </div>
            </div>
            {r.requestNotes && (
              <p className="mb-2 text-sm">{r.requestNotes}</p>
            )}
            <div className="mb-2 text-xs text-muted-foreground">
              <a
                href={
                  r.entityType === 'OPPORTUNITY'
                    ? `/opportunities/${r.entityId}`
                    : `/leads/${r.entityId}`
                }
                className="text-primary hover:underline"
              >
                {t('deletionQueue.open_record')}
              </a>
            </div>
            <textarea
              value={reviewNotes[r.id] ?? ''}
              onChange={(e) =>
                setReviewNotes((p) => ({ ...p, [r.id]: e.target.value }))
              }
              placeholder={t('deletionQueue.review_notes_placeholder')}
              className="mb-2 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
              rows={2}
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={busy === r.id}
                onClick={() => decide(r.id, 'reject')}
              >
                {t('deletionQueue.reject')}
              </Button>
              <Button
                size="sm"
                disabled={busy === r.id}
                onClick={() => decide(r.id, 'approve')}
              >
                {busy === r.id ? t('deletionQueue.approving') : t('deletionQueue.approve')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
